import cx from 'classnames'

import {
  DIMENSION_COLORS,
  DIMENSION_LABELS,
  getHealthLevel,
  ScoreDimension,
  VariantScore,
} from '../types'

type Props = {
  variants: VariantScore[]
  model: string
}

const DIMENSIONS: ScoreDimension[] = ['language', 'settings', 'brand', 'brief']

const average = (values: number[]) =>
  values.length
    ? Math.round(values.reduce((sum, v) => sum + v, 0) / values.length)
    : null

const RunSummary = ({ variants, model }: Props) => {
  const dimensionAverages: Record<ScoreDimension, number | null> = {
    language: average(
      variants
        .map((v) => v.language?.score)
        .filter((s): s is number => s != null)
    ),
    settings: average(
      variants
        .map((v) => v.settings?.score)
        .filter((s): s is number => s != null)
    ),
    brand: average(
      variants.map((v) => v.brand?.score).filter((s): s is number => s != null)
    ),
    brief: average(
      variants.map((v) => v.brief?.score).filter((s): s is number => s != null)
    ),
  }

  const overall = Math.round(
    variants.reduce((sum, v) => sum + v.overall, 0) / variants.length
  )
  const health = getHealthLevel(overall)

  const visibleDimensions = DIMENSIONS.filter(
    (dim) => dimensionAverages[dim] != null
  )

  return (
    <div className="flex items-center gap-5 p-4 bg-white border border-gold-200 rounded-lg">
      <div className="shrink-0">
        <div className="text-xs text-coolGray-400">Run quality score</div>
        <div className="flex items-baseline gap-2 mt-1">
          <span className="text-4xl font-bold tracking-tight text-purple-700">
            {overall}%
          </span>
          <span
            className={cx(
              'text-[10px] font-bold px-2 py-0.5 rounded uppercase',
              health.className
            )}
          >
            {health.label}
          </span>
        </div>
        <div className="text-xs text-coolGray-400 mt-1">
          {variants.length} variant{variants.length !== 1 ? 's' : ''} ·{' '}
          {visibleDimensions.length} dimensions
          {model ? ` · ${model}` : ''}
        </div>
      </div>

      <div className="w-px self-stretch bg-gold-200" />

      <div
        className="grid gap-2 flex-1"
        style={{
          gridTemplateColumns: `repeat(${Math.max(
            visibleDimensions.length,
            1
          )}, minmax(0, 1fr))`,
        }}
      >
        {visibleDimensions.map((dim) => (
          <div
            key={dim}
            className="bg-white border border-gold-200 rounded-md p-3"
            style={{ borderTopWidth: 2, borderTopColor: DIMENSION_COLORS[dim] }}
          >
            <div className="text-xs text-coolGray-400">
              {DIMENSION_LABELS[dim]}
            </div>
            <div
              className="text-lg font-bold"
              style={{ color: DIMENSION_COLORS[dim] }}
            >
              {dimensionAverages[dim]}%
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default RunSummary
